import type { PvpRole } from "../domain/types";

export type ArenaVisualPoint = {
  x: number;
  y: number;
};

export type ArenaVisualTarget = ArenaVisualPoint & {
  radius: number;
};

export type ArenaBackgroundLayout = {
  x: number;
  y: number;
  scale: number;
};

export type ArenaShotTiming = {
  windupMs: number;
  flightMs: number;
  impactAtMs: number;
  impactHoldMs: number;
  keeperDelayMs: number;
  keeperMoveMs: number;
};

export const ARENA_PRESENTATION = {
  windupMs: 420,
  reducedWindupMs: 100,
  shooterFlightMs: 820,
  keeperFlightMs: 860,
  reducedFlightMs: 430,
  impactHoldMs: 1_500,
  reducedImpactHoldMs: 900,
  keeperDelayMs: 105,
  keeperMoveMs: 610,
  cuePulseMs: 1_100,
} as const;

export function arenaShotTiming(role: PvpRole, reducedMotion: boolean): ArenaShotTiming {
  const windupMs = reducedMotion ? ARENA_PRESENTATION.reducedWindupMs : ARENA_PRESENTATION.windupMs;
  const flightMs = reducedMotion
    ? ARENA_PRESENTATION.reducedFlightMs
    : role === "shooter"
      ? ARENA_PRESENTATION.shooterFlightMs
      : ARENA_PRESENTATION.keeperFlightMs;
  return {
    windupMs,
    flightMs,
    impactAtMs: windupMs + flightMs,
    impactHoldMs: reducedMotion ? ARENA_PRESENTATION.reducedImpactHoldMs : ARENA_PRESENTATION.impactHoldMs,
    keeperDelayMs: reducedMotion ? 25 : ARENA_PRESENTATION.keeperDelayMs,
    keeperMoveMs: reducedMotion ? 320 : ARENA_PRESENTATION.keeperMoveMs,
  };
}

const ATTACK_BACKGROUND_SIZE = {
  width: 720,
  height: 1_600,
} as const;

const DEFENSE_BACKGROUND_SIZE = {
  width: 720,
  height: 1_600,
} as const;

const DIRECTION_X = [0.22, 0.5, 0.78] as const;

function coverScale(width: number, height: number, sourceWidth: number, sourceHeight: number): number {
  return Math.max(width / sourceWidth, height / sourceHeight);
}

export function arenaBackgroundLayout(role: PvpRole, width: number, height: number): ArenaBackgroundLayout {
  const source = role === "keeper" ? DEFENSE_BACKGROUND_SIZE : ATTACK_BACKGROUND_SIZE;
  const scale = coverScale(width, height, source.width, source.height);
  return {
    x: width / 2,
    y: height / 2,
    scale,
  };
}

export function arenaTargetLayout(role: PvpRole, width: number, height: number): readonly ArenaVisualTarget[] {
  const compact = height < 700;
  const y = height * (compact ? (role === "keeper" ? 0.58 : 0.565) : role === "keeper" ? 0.565 : 0.475);
  const radius = role === "keeper"
    ? Math.max(32, Math.min(42, width * 0.105))
    : Math.max(34, Math.min(48, width * 0.115));
  return DIRECTION_X.map((x) => ({ x: width * x, y, radius }));
}
